import { useCallback, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { Address } from '@unreserved-frontend-v2/api/generated/graphql/types'
import { AddressResultRow } from '@unreserved-frontend-v2/modules/address-selector/components/address-result-row'
import { AddressSearchLite } from '@unreserved-frontend-v2/modules/address-selector/components/address-search-lite/address-search-lite'
import { FlexCol } from '@unreserved-frontend-v2/ui/flex/flex-col'

export interface AddressInputDesktopLiteProps {
  id?: string

  /** classname for the outermost element of the input */
  containerClassName?: string

  /** inline styles for the outermost element of the input */
  containerInlineStyle?: React.CSSProperties

  /** classes to override on the address entry input */
  inputClassName?: string

  /** label for the input (only used on desktop) */
  label?: React.ReactNode

  /** currently selected Address */
  address?: Address

  placeholder?: string

  isErrored?: boolean

  isRequired?: boolean

  /** callback for when the address is changed */
  onChange: (value?: Address) => void
}

export function AddressInputDesktopLite({
  id,
  containerClassName,
  containerInlineStyle,
  inputClassName,
  label,
  address,
  placeholder,
  isErrored = false,
  isRequired = false,
  onChange,
}: AddressInputDesktopLiteProps) {
  const [currentAddress, setCurrentAddress] = useState<Address | undefined>(address)
  const [editing, setEditing] = useState<boolean>(!Boolean(address))

  const onChangeInternal = useCallback(
    (address?: Address) => {
      setCurrentAddress(address)
      setEditing(false)
      onChange(address)
    },
    [onChange]
  )

  const handleDelete = useCallback(() => {
    setCurrentAddress(undefined)
    setEditing(true)
    onChange()
  }, [onChange])

  const handleInputBlur = useCallback(() => {
    if (!!currentAddress) {
      setEditing(false)
    }
  }, [currentAddress])

  return (
    <FlexCol id={id} className={containerClassName} style={containerInlineStyle}>
      {editing ? label : null}

      {currentAddress && !editing && !isErrored ? (
        <AddressResultRow
          containerClassName="min-h-[72px]"
          address={currentAddress}
          showEditControls={true}
          allowDelete={!isRequired}
          onEdit={() => setEditing(true)}
          onDelete={handleDelete}
          mapSize="big"
        />
      ) : (
        <AddressSearchLite
          containerClassName="relative"
          inputClassName={twMerge(
            inputClassName,
            isErrored
              ? 'border-red-600 bg-red-50 text-red-600 !placeholder-red-400 outline-red-600'
              : '-mr-2 overflow-hidden focus:outline-none focus:border-primary'
          )}
          onAddressSelected={onChangeInternal}
          inputPlaceholder={placeholder}
          address={editing ? currentAddress : undefined}
          onInputBlur={handleInputBlur}
        />
      )}
    </FlexCol>
  )
}

export default AddressInputDesktopLite
